// React Imports
import { useState, useEffect } from "react";

// External Libraries
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Dialog } from "primereact/dialog";

// Toolkit Imports
import { forgotPassword, confirmNewPassword } from "sj-ui-toolkit/src/integrations";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { signOut } from "sj-ui-toolkit/src/utils";

const COUNTDOWN_SECONDS = 5;

export function PasswordResetSection({ isResetting, onToggleReset, userEmail }) {
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!showSignOutModal) return;

    if (countdown <= 0) {
      signOut();
      return;
    }

    const timer = setTimeout(() => setCountdown(count => count - 1), 1000);

    return () => clearTimeout(timer);
  }, [showSignOutModal, countdown, signOut]);

  const sendConfirmationCode = async () => {
    setIsSendingCode(true);

    try {
      await forgotPassword(userEmail);
      onToggleReset(true);
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Confirmation code sent. Please check your email.",
        life: 5000,
      });
    } catch (error) {
      console.error("Error sending confirmation code:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: error.message || "Error sending confirmation code",
        life: 5000,
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleResetClick = async e => {
    e.preventDefault();
    setIsResettingPassword(true);

    try {
      await confirmNewPassword(userEmail, code, newPassword);
      resetFields();
      setShowSignOutModal(true);
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: error || "Error resetting password",
        life: 5000,
      });
      console.error("Error confirming new password:", error);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const modalFooter = (
    <Button label="Sign out now" icon="pi pi-sign-out" onClick={signOut} autoFocus />
  );

  const resetFields = () => {
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const passwordsFilled = newPassword.length > 0 && confirmPassword.length > 0;
  const doPasswordsMatch = passwordsFilled && newPassword === confirmPassword;
  const canSubmit = code && doPasswordsMatch;

  if (!isResetting) {
    return (
      <section aria-labelledby="password-heading">
        <h2 id="password-heading" className="atlas-h5 mb-4">
          Password
        </h2>
        <Button
          severity="secondary"
          label="Reset Password"
          onClick={sendConfirmationCode}
          loading={isSendingCode}
          disabled={isSendingCode}
        />
      </section>
    );
  }

  return (
    <section>
      <form className="flex flex-col gap-4" onSubmit={handleResetClick}>
        <InputText
          id="verification-code"
          placeholder="Verification code"
          value={code}
          onChange={e => setCode(e.target.value)}
          className="w-[300px]"
          aria-required="true"
          aria-label="Verification code"
        />
        <Password
          id="new-password"
          placeholder="New password"
          toggleMask
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          feedback={false}
          aria-required="true"
          aria-label="New password"
          pt={{
            input: { className: "w-[300px]" },
            hideIcon: { className: "absolute right-2" },
            showIcon: { className: "absolute right-2" },
          }}
        />
        <Password
          id="confirm-password"
          placeholder="Confirm new password"
          toggleMask
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          feedback={false}
          invalid={!doPasswordsMatch && passwordsFilled}
          aria-required="true"
          aria-label="Confirm new password"
          aria-invalid={!doPasswordsMatch && passwordsFilled}
          pt={{
            input: { className: "w-[300px]" },
            hideIcon: { className: "absolute right-2" },
            showIcon: { className: "absolute right-2" },
          }}
        />
        <div className="flex gap-4">
          <Button
            type="submit"
            label="Reset Password"
            loading={isResettingPassword}
            disabled={!canSubmit || isResettingPassword}
          />
        </div>
      </form>
      <Dialog
        visible={showSignOutModal}
        onHide={() => {}}
        closable={false}
        draggable={false}
        footer={modalFooter}
        header="Password Updated"
        modal
        maskStyle={{ backdropFilter: "blur(4px)" }}
        style={{ width: "28rem" }}
      >
        <p>Your password has been updated. You'll be signed out in {countdown} seconds.</p>
      </Dialog>
    </section>
  );
}
